// #21 Toggle: a <button role="switch" aria-checked> 34 × 20 px, labelled by its visible words (a <label>, so a click on
// the words toggles too) or, with hideLabel, by aria-label. `fixed`: always on and cannot change (aria-disabled, the
// green form). `blocked` (DC-24): dashed, aria-disabled, the inline reason and the mirrored tooltip. mode="view": the
// words and his *Yes* / *No* (G §16), no control.

import { useId, type ReactNode } from "react";
import { ACCESS_LABELS } from "../labels";
import { cx } from "../lib/cx";
import { useHs } from "../lib/hs";
import { Tooltip } from "../lib/Tooltip";
import { useBlocked, type ControlProps } from "./Button";

export interface ToggleProps extends ControlProps {
  /** His words (*Executive participation*, *Add to talent community*, *Show previous versions*). */
  label: string;
  checked: boolean;
  onChange?: ((checked: boolean) => void) | undefined;
  /** Always on; it cannot be changed here (aria-disabled). */
  fixed?: boolean | undefined;
  /** The words are not drawn; they name the switch through aria-label. */
  hideLabel?: boolean | undefined;
  /** Words after the switch, read as its description (a meta line). */
  sub?: ReactNode | undefined;
  id?: string | undefined;
  className?: string | undefined;
}

export function Toggle({ label, checked, onChange, fixed, hideLabel, sub, can, blocked, mode, hs, id, className }: ToggleProps) {
  const generated = useId();
  const controlId = id ?? `${generated}-sw`;
  const subId = `${generated}-sub`;
  const labelId = `${generated}-l`;
  const hot = useHs(hs);
  const block = useBlocked(blocked, sub ? subId : undefined);
  if (can === false) return null;
  const on = checked || Boolean(fixed);
  if (mode === "view") {
    return (
      <span className={cx("mc-toggle-w", "mc-toggle-view", className)}>
        <span className="mc-toggle-l">{label}</span>
        <b>{on ? ACCESS_LABELS.Yes : ACCESS_LABELS.No}</b>
      </span>
    );
  }
  const inert = Boolean(fixed || blocked);
  const button = (
    <button
      type="button"
      role="switch"
      id={controlId}
      aria-checked={on}
      aria-label={hideLabel ? label : undefined}
      aria-labelledby={hideLabel ? undefined : labelId}
      aria-describedby={block.describedBy}
      aria-disabled={inert ? true : undefined}
      className={cx("mc-toggle", on && "mc-on", fixed && "mc-toggle-fixed", blocked && "mc-dis")}
      onClick={() => { if (!inert) onChange?.(!checked); }}
      {...hot}
    />
  );
  return (
    <span className={cx("mc-toggle-w", className)}>
      {blocked ? <Tooltip label={blocked.reason} mode="mirror">{button}</Tooltip> : button}
      {hideLabel ? null : <label className="mc-toggle-l" id={labelId} htmlFor={controlId}>{label}</label>}
      {sub ? <span className="mc-toggle-s" id={subId}>{sub}</span> : null}
      {block.reason}
    </span>
  );
}
